import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import dayjs from 'dayjs';
import combinedActions from '~redux/combinedActions';
import { initialState as goldInitialState } from '~redux/slices/gold';
import * as Styles from './__earmarkApprovalSearchAndFilterBar.module.scss';
import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';
import { TGetEarmarkConversionListingFilters } from '~types/feat/gold';
import { MetalTypeEnum } from '~utils/enums';

const EarmarkApprovalSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.gold.earmarkConversionListing.filters);
    const initialFilters = goldInitialState.earmarkConversionListing.filters;
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TGetEarmarkConversionListingFilters) => dispatch(combinedActions.goldSetEarmarkConversionListingFilters(inputFilters));

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);
    const handleSearchClick = () => dispatch(combinedActions.goldGetEarmarkConversionListingAttempt({ index: 0, ...filters }));

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
        });
        dispatch(combinedActions.goldGetEarmarkConversionListingAttempt({ index: 0 }));
        dispatch(combinedActions.goldGetEarmarkConversionListingAttempt({ index: 0 }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerName,
                setValue: (event) => setFilters({ ...filters, customerName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Customer ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.idNo,
                setValue: (event) => setFilters({ ...filters, idNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};
export default EarmarkApprovalSearchAndFilterBar;

